import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  IconButton,
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Smartphone as MobileIcon,
  Computer as DesktopIcon,
  Delete as UninstallIcon,
  Refresh as ReinstallIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { InstallationGuide } from './InstallationGuide';
import {
  usePWA,
  useDeviceDetection,
  getPlayStoreUrl,
  getAppStoreUrl,
} from '../contexts/PWAContext';

interface InstallationOptionsScreenProps {
  open: boolean;
  onClose: () => void;
}

type InstructionItem =
  | string
  | { text: string; link: { url: string; text: string } };

export const InstallationOptionsScreen: React.FC<
  InstallationOptionsScreenProps
> = ({ open, onClose }) => {
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showUninstallDialog, setShowUninstallDialog] = useState(false);
  const [isReinstalling, setIsReinstalling] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const deviceInfo = useDeviceDetection();
  const { pwaState, triggerInstallPrompt, resetInstallationStatus } = usePWA();

  const isPWAInstalled = pwaState.isInstalled;
  const canInstallPWA = pwaState.canInstall;
  const isStandalone = pwaState.isStandalone;

  // Calculate installation options
  const installationOptions = (() => {
    const {
      isIOS,
      isAndroid,
      isDesktop,
      canInstallPWA: deviceCanInstallPWA,
      canInstallMobileApp,
      mobileAppCompatible,
      browser,
    } = deviceInfo;

    // Don't show any installation options if app is already running as PWA or installed
    if (isStandalone || isPWAInstalled) {
      return {
        showPWAInstall: false,
        showMobileAppInstall: false,
        showIOSInstructions: false,
        showAndroidInstructions: false,
        showDesktopInstructions: false,
        mobileAppStore: false,
      };
    }

    return {
      showPWAInstall: deviceCanInstallPWA,
      showMobileAppInstall: canInstallMobileApp && mobileAppCompatible,
      showIOSInstructions:
        isIOS &&
        deviceCanInstallPWA &&
        (!mobileAppCompatible || browser !== 'Safari'),
      showAndroidInstructions:
        isAndroid && deviceCanInstallPWA && !mobileAppCompatible,
      showDesktopInstructions: isDesktop && deviceCanInstallPWA,
      mobileAppStore: canInstallMobileApp && mobileAppCompatible,
    };
  })();

  // Get platform-specific PWA benefits
  const getPlatformSpecificBenefits = () => {
    const { isIOS, isAndroid, isDesktop, browser } = deviceInfo;

    if (isDesktop) {
      return {
        title: 'Why install on your Desktop?',
        benefits: [
          'Runs in its own window, separate from browser tabs',
          'Appears in your taskbar/dock like a regular application',
          'Can be pinned to your start menu for quick access',
          'Faster loading with cached resources',
          'Can notify you of alerts even when closed',
          'Auto-updates like a regular application',
          'Enhanced camera access for barcode scanning',
        ],
      };
    }

    if (isIOS) {
      return {
        title: 'Why Install locally on iOS?',
        benefits: [
          'Add to home screen for quick access',
          'Works offline for cached features',
          'Automatic updates without App Store',
          'Same functionality as native app',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
          '**Environment-facing camera** selection by default',
          '**Smart platform detection** with mobile optimizations',
          '**Automatic error recovery** from update issues',
          'Biometric authentication support',
          'Push notifications when app is closed',
        ],
      };
    }

    if (isAndroid) {
      return {
        title: 'Why Install locally on Android?',
        benefits: [
          'Add to home screen for quick access',
          'Works offline for cached features',
          'Automatic updates without Play Store',
          'Same functionality as native app',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
          '**Environment-facing camera** selection by default',
          '**Smart platform detection** with mobile optimizations',
          '**Automatic error recovery** from update issues',
          'Biometric authentication support',
          'Push notifications when app is closed',
        ],
      };
    }

    // Fallback for other platforms
    return {
      title: 'Why Install locally?',
      benefits: [
        'Quick access from home screen',
        'Works offline for cached features',
        'Automatic updates',
        'Same functionality as native app',
        '**Enhanced camera access** for barcode scanning',
        '**Image processing** for better QR code detection',
        '**Smart platform detection** with automatic optimization',
        '**Automatic error recovery** from update issues',
        'Push notifications when app is closed',
      ],
    };
  };

  const handlePWAInstall = () => {
    if (isReinstalling) {
      // For reinstallation, show the guide with reinstall instructions
      setIsReinstalling(true);
      setShowInstallGuide(true);
    } else {
      // For fresh installation, trigger the PWA install prompt
      triggerInstallPrompt();
    }
  };

  const handleMobileAppStore = () => {
    // Direct link to app store based on device
    if (deviceInfo.isIOS) {
      // Replace with your actual App Store URL
      window.open(getAppStoreUrl(), '_blank');
    } else if (deviceInfo.isAndroid) {
      // Replace with your actual Play Store URL
      window.open(getPlayStoreUrl(), '_blank');
    }
  };

  const handleUninstall = () => {
    setShowUninstallDialog(true);
  };

  const handleResetAfterUninstall = () => {
    resetInstallationStatus();
    setShowUninstallDialog(false);
    onClose();
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy URL:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const getUninstallInstructions = (): InstructionItem[] => {
    const { isIOS, isAndroid, isDesktop, browser } = deviceInfo;

    if (isIOS) {
      return [
        '1. Find the app icon on your home screen',
        '2. Long press the app icon until it starts wiggling',
        '3. Tap the "X" button that appears on the app icon',
        '4. Tap "Delete" to confirm removal',
        '5. Return to this screen and press the "Confirm Uninstall" button below',
      ];
    }

    if (isAndroid) {
      return [
        '1. Find the app icon on your home screen or app drawer',
        '2. Long press the app icon',
        '3. Drag it to "Uninstall" or tap "App info" then "Uninstall"',
        '4. Tap "OK" to confirm',
        '5. Return to this screen and press the "Confirm Uninstall" button below',
      ];
    }

    if (isDesktop) {
      // Safari-specific instructions for macOS
      if (browser === 'Safari') {
        return [
          '1. Open Safari and go to Safari > Settings (or Preferences)',
          '2. Click on the "Websites" tab',
          '3. Select "Web Applications" from the left sidebar',
          '4. Find the app in the list and click the "Remove" button',
          '5. Alternatively, you can drag the app from Applications folder to Trash',
          '6. Return to this screen and press the "Confirm Uninstall" button below',
        ];
      }

      // Chrome-specific instructions for desktop
      if (browser === 'Chrome') {
        // Chrome on macOS installs PWAs as native apps, different from Windows/Linux
        const isMacOS = navigator.userAgent.includes('Mac OS X');
        if (isMacOS) {
          return [
            {
              text: '1. Open Chrome and manually type or paste ',
              link: { url: 'chrome://apps/', text: 'chrome://apps/' },
            },
            '2. Find the Embue Super app in the list of installed apps',
            '3. Right-click on the app icon and select "Remove from Chrome"',
            '4. Click the checkbox to remove the associated data too',
            '5. Click "Remove" to confirm',
            '6. Alternatively, drag the app from Applications folder to Trash',
            '7. Return to this screen and press the "Confirm Uninstall" button below',
          ];
        } else {
          return [
            {
              text: '1. Open Chrome and manually type or paste ',
              link: { url: 'chrome://apps/', text: 'chrome://apps/' },
            },
            '2. Find the app in the list of installed apps',
            '3. Right-click on the app icon and select "Remove from Chrome"',
            '4. Click the checkbox to remove the associated data too',
            '5. Click "Remove" to confirm',
            '6. Return to this screen and press the "Confirm Uninstall" button below',
          ];
        }
      }

      // Firefox-specific instructions
      if (browser === 'Firefox') {
        return [
          '1. Open Firefox and go to about:apps',
          '2. Find the app in the list of installed apps',
          '3. Click the "Remove" button next to the app',
          '4. Confirm the removal',
          '5. Return to this screen and press the "Confirm Uninstall" button below',
        ];
      }

      // Edge-specific instructions
      if (browser === 'Edge') {
        return [
          '1. Open Edge and go to edge://apps/',
          '2. Find the app in the list of installed apps',
          '3. Right-click on the app icon and select "Remove from Edge"',
          '4. Click "Remove" to confirm',
          '5. Return to this screen and press the "Confirm Uninstall" button below',
        ];
      }

      // Generic desktop instructions for other browsers
      return [
        "1. Open your browser and go to the browser's app management page",
        '2. Find the app in the list of installed apps',
        '3. Click the "Remove" or "Uninstall" option',
        '4. Confirm the removal',
        '5. Return to this screen and press the "Confirm Uninstall" button below',
      ];
    }

    // Fallback for other platforms
    return [
      '1. Find the app in your applications list',
      '2. Right-click and select "Uninstall" or "Remove"',
      '3. Follow the uninstall process',
      '4. Return to this screen and press the "Confirm Uninstall" button below',
    ];
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Local Installation Options</Typography>
            <IconButton
              sx={{ alignSelf: 'flex-end', marginRight: '-1rem' }}
              onClick={onClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container id="peter_x" spacing={0}>
            {/* PWA Installation Card */}
            <Grid item xs={12} md={12}>
              <Card>
                <CardContent
                  sx={{
                    '&.MuiCardContent-root': {
                      padding: '0px 0px 20px 0px',
                      margin: '0px',
                    },
                  }}
                >
                  <div>
                    {!isPWAInstalled
                      ? 'For a better, more convenient experience, you can install our application directly on your desktop.'
                      : 'Our application is already installed on your desktop. You can reinstall or uninstall it below.'}
                  </div>
                </CardContent>
                <CardActions>
                  <Button
                    onClick={handlePWAInstall}
                    variant="contained"
                    startIcon={
                      isPWAInstalled ? <ReinstallIcon /> : <InstallIcon />
                    }
                    disabled={!canInstallPWA && !isPWAInstalled}
                    fullWidth
                  >
                    {isPWAInstalled
                      ? 'Reinstall Embue Super'
                      : 'Install Embue Super'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Mobile App Store Card */}
            {installationOptions.mobileAppStore && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <MobileIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        Alternative: Mobile App
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Alternative option for users who prefer app store
                      installation. Requires iOS 18+ or Android 8+. Local
                      installation is recommended for most users due to
                      automatic updates, enhanced multi-platform scanning
                      capabilities, smart platform detection, and wider
                      compatibility.
                    </Typography>

                    {deviceInfo.isIOS &&
                      deviceInfo.osVersion &&
                      parseFloat(deviceInfo.osVersion) < 17.0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            Your iOS version ({deviceInfo.osVersion}) may not be
                            compatible with our latest app. Consider using the
                            local installation option instead.
                          </Typography>
                        </Alert>
                      )}
                  </CardContent>
                  <CardActions>
                    <Button
                      onClick={handleMobileAppStore}
                      variant="outlined"
                      startIcon={<MobileIcon />}
                      fullWidth
                    >
                      {deviceInfo.isIOS ? 'App Store' : 'Google Play'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            )}

            {/* Uninstall Option - Only show if PWA is installed */}
            {isPWAInstalled && (
              <Grid item xs={12}>
                <Card sx={{ borderColor: 'error.main', border: 1 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <UninstallIcon sx={{ mr: 1, color: 'error.main' }} />
                      <Typography variant="h6" color="error.main">
                        Remove Local Installation
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Remove the local installation of Super from your device if
                      you no longer need it.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      onClick={handleUninstall}
                      variant="outlined"
                      color="error"
                      startIcon={<UninstallIcon />}
                      fullWidth
                    >
                      Remove Local installation
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Platform-Specific Information Section - Only show when not in PWA mode */}
          {!isStandalone && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info">
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    {getPlatformSpecificBenefits().title}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {getPlatformSpecificBenefits().benefits.map(
                      (benefit, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          component="li"
                          sx={{ mb: 0.5 }}
                        >
                          {benefit}
                        </Typography>
                      ),
                    )}
                  </Box>
                </Box>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Installation Guide Dialog */}
      <InstallationGuide
        open={showInstallGuide}
        onClose={() => {
          setShowInstallGuide(false);
          setIsReinstalling(false);
        }}
        isReinstalling={isReinstalling}
      />

      {/* Uninstall Instructions Dialog */}
      <Dialog
        open={showUninstallDialog}
        onClose={() => setShowUninstallDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <UninstallIcon sx={{ mr: 1, color: 'error.main' }} />
            <Typography variant="h6" color="error.main">
              Remove Local Installation Instructions
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Follow these steps to remove the local installation from your{' '}
            {deviceInfo.deviceType} device:
          </Typography>

          <Box sx={{ mt: 2, mb: 2 }}>
            {getUninstallInstructions().map((instruction, index) => {
              if (
                typeof instruction === 'object' &&
                'link' in instruction &&
                instruction.link
              ) {
                return (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Typography variant="body2" component="span">
                      {instruction.text}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        ml: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="code"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)',
                          padding: '2px 6px',
                          borderRadius: 1,
                          fontSize: '0.875rem',
                        }}
                      >
                        {instruction.link.text}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyUrl(instruction.link.url)}
                        sx={{
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                        title="Copy URL to clipboard"
                      >
                        {copiedUrl ? (
                          <CheckIcon fontSize="small" color="success" />
                        ) : (
                          <CopyIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                    <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                      <Typography variant="caption">
                        Note: chrome:// URLs cannot be opened directly from web
                        pages. Please copy the URL above and paste it into
                        Chrome's address bar, or type it manually.
                      </Typography>
                    </Alert>
                  </Box>
                );
              }
              return (
                <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                  {typeof instruction === 'string'
                    ? instruction
                    : instruction.text}
                </Typography>
              );
            })}
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              After following the steps above, return to this screen and press
              the "Confirm Uninstall" button to reset the installation status.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUninstallDialog(false)}>Cancel</Button>
          <Button
            onClick={handleResetAfterUninstall}
            variant="contained"
            color="error"
            startIcon={<UninstallIcon />}
          >
            Confirm Uninstall
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
